import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Select from '~/components/Select'
import { useToast } from '~/components/useToast'
import type { ToastPositionType } from '~/types/useModal'

const UseToastPage: Component = () => {
  const { addToast, setPosition } = useToast()
  const [currentPos, setCurrentPos] = createSignal<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center'>('top-right')

  const posOptions = [
    { value: 'top-left', title: 'Top Left' },
    { value: 'top-right', title: 'Top Right' },
    { value: 'top-center', title: 'Top Center' },
    { value: 'bottom-left', title: 'Bottom Left' },
    { value: 'bottom-right', title: 'Bottom Right' },
    { value: 'bottom-center', title: 'Bottom Center' }
  ]

  return (
    <>
      <Title>useToast - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">useToast</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Feedback no obstructivo. El sistema de Toasts proporciona notificaciones temporales de alta visibilidad para confirmar acciones del sistema, errores de red o actualizaciones de estado en
            tiempo real.
          </p>
        </div>

        <div class="space-y-12">
          {/* Configurador de Posición */}
          <Card>
            <Card.Header
              title="Orquestación Espacial"
              subtitle="Configura el punto de anclaje de las notificaciones para optimizar la jerarquía visual."
              icon={<Icon icon="material-symbols-light:left-panel-open-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-col md:flex-row items-center gap-8 p-8 bg-surface-muted rounded-3xl border border-border">
                <div class="w-full md:w-64 space-y-4">
                  <div class="text-xs font-bold text-foreground-subtle uppercase tracking-widest text-center md:text-left">Anclaje Global</div>
                  <Select
                    value={currentPos()}
                    options={posOptions}
                    onChange={v => {
                      setCurrentPos(v as ToastPositionType)
                      setPosition?.(v as ToastPositionType)
                    }}
                  />
                </div>
                <div class="flex-1 grid grid-cols-2 lg:grid-cols-3 gap-3">
                  <Button
                    color="success"
                    onClick={() =>
                      addToast({
                        type: 'success',
                        title: 'Envío Exitoso',
                        description: 'El paquete ha sido despachado.'
                      })
                    }
                    icon="material-symbols-light:check-circle-outline"
                  />
                  <Button
                    color="danger"
                    onClick={() =>
                      addToast({
                        type: 'danger',
                        title: 'Error de Red',
                        description: 'No se pudo sincronizar con el nodo.'
                      })
                    }
                    icon="material-symbols-light:warning-outline"
                  />
                  <Button color="info" onClick={() => addToast({ type: 'info', title: 'Sistema', description: 'Actualización v4.2 disponible.' })} icon="material-symbols-light:info-outline" />
                  <Button
                    color="warning"
                    onClick={() =>
                      addToast({
                        type: 'warning',
                        title: 'Advertencia',
                        description: 'Cuota de almacenamiento al 90%.'
                      })
                    }
                    icon="material-symbols-light:warning-outline"
                  />
                  <Button
                    variant="outlined"
                    onClick={() => addToast({ type: 'default', title: 'Notificación', description: 'Mensaje de sistema genérico.' })}
                    icon="material-symbols-light:notifications-outline"
                  />
                  <Button variant="soft" color="primary" onClick={() => addToast({ type: 'success', title: 'Tarea Terminada' })} icon="material-symbols-light:emoji-events" />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Toasts con Acción */}
          <Card>
            <Card.Header
              title="Interacción en Notificación"
              subtitle="Permite al usuario resolver problemas o navegar directamente desde el aviso."
              icon={<Icon icon="material-symbols-light:star-shine" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-wrap gap-4 p-8 bg-primary-soft rounded-3xl border border-primary/25 border-dashed">
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() =>
                    addToast({
                      type: 'info',
                      title: 'Seguridad',
                      description: 'Se ha detectado un inicio de sesión inusual.',
                      action: { label: 'Revisar Actividad', onClick: () => alert('Checking...') }
                    })
                  }
                >
                  Reporte de Seguridad
                </Button>
                <Button
                  color="success"
                  variant="soft"
                  onClick={() =>
                    addToast({
                      type: 'success',
                      title: 'Reserva Confirmada',
                      description: 'Tu estancia en Bali ha sido pagada.',
                      action: { label: 'Ver Itinerario', onClick: () => alert('Opening...') }
                    })
                  }
                >
                  Reserva de Viaje
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="Hook API"
              subtitle="Métodos para inyectar notificaciones en la cola global."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Método</th>
                      <th class="px-6 py-4 border-b">Parámetros</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">addToast(options)</td>
                      <td class="px-6 py-4 italic text-xs">ToastOptions</td>
                      <td class="px-6 py-4 italic">Agrega una notificación a la cola de renderizado.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">setPosition(pos)</td>
                      <td class="px-6 py-4 italic text-xs">ToastPositionType</td>
                      <td class="px-6 py-4 text-balance">Cambia la ubicación global de todos los toasts activos.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">toast.success(t, m?)</td>
                      <td class="px-6 py-4 italic text-xs">string, string?</td>
                      <td class="px-6 py-4 italic">Helper estático para disparos rápidos de éxito.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default UseToastPage
